/**
 * Audio Recording Module for InterPals Private Messages
 * Handles microphone recording, preview playback, upload, and message sending
 */

import {
    AudioUploadConfig,
    AudioUploadErrors,
    uploadAudio,
    getSupportedMimeType
} from '../modules/audio-upload-core.js';
import { t } from '../modules/i18n.js';
import { formatMinutesSeconds } from '../modules/minutes-seconds.js';

const ENDPOINT = '/app/messages/send-message';

/**
 * Chrome duration bug workaround
 * Chrome sometimes reports Infinity duration for recorded audio blobs
 * @param {HTMLAudioElement} audioEl
 * @returns {Promise<void>}
 */
function fixChromeDuration(audioEl) {
    return new Promise((resolve) => {
        if (audioEl.duration !== Infinity && !isNaN(audioEl.duration)) {
            resolve();
            return;
        }
        audioEl.currentTime = 1e101;
        audioEl.addEventListener('timeupdate', function handler() {
            audioEl.removeEventListener('timeupdate', handler);
            audioEl.currentTime = 0;
            resolve();
        }, { once: true });
    });
}

/**
 * Main audio recorder class
 */
class AudioRecorder {
    constructor() {
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.audioBlob = null;
        this.audioId = null;
        this.stream = null;
        this.isRecording = false;
        this.isPlaying = false;
        this.isUploading = false;
        this.sendAfterStop = false;
        this.elapsedTime = 0;
        this.timerInterval = null;
        this.mimeType = '';
        this.elements = {};

        // Live waveform state
        this.audioContext = null;
        this.analyser = null;
        this.analyserSource = null;
        this.analyserData = null;

        this.init();
    }

    init() {
        this.mimeType = getSupportedMimeType();
        this.cacheElements();
        this.bindEvents();
        this.checkBrowserSupport();
    }

    cacheElements() {
        this.elements = {
            micBtn: $('#mic_btn'),
            composeForm: $('#threads_right_bottom form'),
            recordContainer: $('#audio_record_container'),
            cancelBtn: $('#audio_cancel_btn'),
            stopBtn: $('#audio_stop_btn'),
            playBtn: $('#audio_play_btn'),
            sendBtn: $('#audio_send_btn'),
            timer: $('#audio_timer'),
            recIndicator: $('#audio_rec_indicator'),
            waveformLive: $('#audio_waveform_live'),
            audioPreview: $('#audio_preview_element'),
            controls: $('#audio_controls'),
            sendingState: $('#audio_sending_state'),
            audioIdInput: $('#audio_id')
        };
    }

    bindEvents() {
        this.elements.micBtn.on('click', () => this.startRecording());
        this.elements.cancelBtn.on('click', () => this.cancelRecording());
        this.elements.stopBtn.on('click', () => this.stopRecording());
        this.elements.playBtn.on('click', () => this.togglePlayback());
        this.elements.sendBtn.on('click', () => this.handleSend());

        // Stop playback when audio ends
        this.elements.audioPreview.on('ended', () => {
            this.isPlaying = false;
            this.showPlayButton();
        });

        // Clean up on thread close
        $(document).on('thread_closed', () => this.cancelRecording());
    }

    checkBrowserSupport() {
        const supported = navigator.mediaDevices &&
            navigator.mediaDevices.getUserMedia &&
            typeof MediaRecorder !== 'undefined';

        if (!supported) {
            this.elements.micBtn.addClass('hidden');
        }
    }

    // --- Recording lifecycle ---

    async startRecording() {
        if (this.isRecording || this.isUploading) return;

        // Clear reply state — audio messages don't support reply attachments
        if (window.cancelReply) window.cancelReply();
        // A playing clip would sound into the recording.
        window.pauseVideoPlayback?.();

        try {
            this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            window.showError?.(AudioUploadErrors.MICROPHONE_DENIED) || console.error(AudioUploadErrors.MICROPHONE_DENIED);
            return;
        }

        this.audioChunks = [];
        this.audioBlob = null;
        this.audioId = null;
        this.sendAfterStop = false;

        try {
            const options = this.mimeType ? { mimeType: this.mimeType } : undefined;
            this.mediaRecorder = new MediaRecorder(this.stream, options);
        } catch {
            this.stopStream();
            window.showError?.(AudioUploadErrors.NOT_SUPPORTED) || console.error(AudioUploadErrors.NOT_SUPPORTED);
            return;
        }

        this.mediaRecorder.ondataavailable = (e) => {
            if (e.data.size > 0) this.audioChunks.push(e.data);
        };

        this.mediaRecorder.onstop = () => this.onRecordingStopped();

        // Set up AnalyserNode for live waveform
        this.setupAnalyser(this.stream);

        this.mediaRecorder.start();
        this.isRecording = true;
        this.elapsedTime = 0;

        this.showRecordingUI();
        this.startTimer();
    }

    stopRecording() {
        if (!this.mediaRecorder || this.mediaRecorder.state === 'inactive') return;
        this.mediaRecorder.stop();
        this.stopStream();
    }

    onRecordingStopped() {
        this.isRecording = false;
        this.stopTimer();

        const actualMimeType = this.mediaRecorder?.mimeType || this.mimeType || 'audio/webm';
        this.audioBlob = new Blob(this.audioChunks, { type: actualMimeType });

        const url = URL.createObjectURL(this.audioBlob);
        this.elements.audioPreview[0].src = url;

        fixChromeDuration(this.elements.audioPreview[0]);

        this.showPreviewUI();

        if (this.sendAfterStop) {
            this.sendAfterStop = false;
            this.uploadAndSend();
        }
    }

    cancelRecording() {
        if (this.isPlaying) {
            this.elements.audioPreview[0].pause();
            this.elements.audioPreview[0].currentTime = 0;
            this.isPlaying = false;
        }
        if (this.isRecording) {
            this.stopRecording();
        }
        this.cleanup();
        this.hideRecordingUI();
    }

    // --- Send flow ---

    async handleSend() {
        if (this.isUploading) return;

        // If still recording and long enough, stop then send
        if (this.isRecording && this.elapsedTime >= AudioUploadConfig.MIN_DURATION) {
            this.sendAfterStop = true;
            this.stopRecording();
            return;
        }

        // If we have a recorded blob, upload and send
        if (this.audioBlob) {
            await this.uploadAndSend();
        }
    }

    async uploadAndSend() {
        if (this.elapsedTime < AudioUploadConfig.MIN_DURATION) {
            window.showWarning?.(AudioUploadErrors.TOO_SHORT) || console.warn(AudioUploadErrors.TOO_SHORT);
            return;
        }

        this.isUploading = true;
        this.showUploadingUI();

        try {
            const result = await uploadAudio({
                blob: this.audioBlob
            });

            this.audioId = String(result.id);
            this.uploadResult = result;
            this.elements.audioIdInput.val(this.audioId);

            await this.sendAudioMessage();
        } catch (error) {
            console.error('Audio upload/send failed:', error);
            window.showError?.(error.message || AudioUploadErrors.SEND_FAILED) || console.error(error.message || AudioUploadErrors.SEND_FAILED);
        } finally {
            this.isUploading = false;
            this.cleanup();
            this.hideRecordingUI();
        }
    }

    sendAudioMessage() {
        const threadId = $('#send_thread_id').val();

        return new Promise((resolve, reject) => {
            window.pm_ajax_request_active = 1;

            $.ajax({
                type: 'POST',
                url: ENDPOINT,
                data: {
                    action: 'send_message',
                    thread: threadId,
                    message: '',
                    audio_id: this.audioId
                },
                dataType: 'json'
            }).done((response) => {
                // Auth/CSRF/onboarding gate returns {redirect: url} for a lapsed
                // session. The global ajaxComplete handler navigates, so neither
                // resolve nor reject to avoid flashing a send-failure error.
                if (response && typeof response.redirect === 'string' && response.redirect) {
                    return;
                }
                if (response.success) {
                    window.append_msgs(response.body, $('#threads_right'));

                    // Inject waveform data from upload response into the newly appended player
                    if (this.uploadResult?.waveform) {
                        const players = document.querySelectorAll('.audio-player:not([data-initialized])');
                        const lastPlayer = players[players.length - 1];
                        if (lastPlayer) {
                            const wf = this.uploadResult.waveform;
                            const waveformData = typeof wf === 'string' ? wf : JSON.stringify(wf);
                            lastPlayer.setAttribute('data-waveform', waveformData);
                        }
                    }

                    // Initialize any new audio players
                    window.initAudioPlayers?.();

                    // Clear reply state if present
                    if (window.cancelReply) window.cancelReply();

                    // Update thread in sidebar if provided
                    if (response.updated_thread && response.thread_id) {
                        const $existingThread = $(`#thread_${response.thread_id}`);
                        if ($existingThread.length) {
                            $existingThread.replaceWith(response.updated_thread);
                        }
                    }

                    resolve(response);
                } else {
                    reject(new Error(response.error || AudioUploadErrors.SEND_FAILED));
                }
            }).fail((xhr, status, error) => {
                console.error('Audio message send failed:', status, error);
                reject(new Error(AudioUploadErrors.SEND_FAILED));
            }).always(() => {
                window.pm_ajax_request_active = 0;
            });
        });
    }

    // --- Playback ---

    togglePlayback() {
        const audioEl = this.elements.audioPreview[0];

        if (this.isPlaying) {
            audioEl.pause();
            audioEl.currentTime = 0;
            this.isPlaying = false;
            this.showPlayButton();
        } else {
            audioEl.play();
            this.isPlaying = true;
            this.showStopPlaybackButton();
        }
    }

    // --- Live waveform ---

    setupAnalyser(stream) {
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 2048;
            this.analyser.smoothingTimeConstant = 0.3;
            this.analyserSource = this.audioContext.createMediaStreamSource(stream);
            this.analyserSource.connect(this.analyser);
            this.analyserData = new Uint8Array(this.analyser.fftSize);
        } catch {
            this.analyser = null;
        }
    }

    teardownAnalyser() {
        if (this.analyserSource) {
            this.analyserSource.disconnect();
            this.analyserSource = null;
        }
        if (this.audioContext) {
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
        this.analyser = null;
        this.analyserData = null;
    }

    /**
     * Sample current mic amplitude and append a bar to the live waveform.
     * Uses RMS of time-domain data to match the server-side waveform style.
     */
    sampleWaveform() {
        if (!this.analyser || !this.analyserData) return;

        this.analyser.getByteTimeDomainData(this.analyserData);

        // RMS amplitude from time-domain waveform (centered at 128)
        const data = this.analyserData;
        let sumSquares = 0;
        for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sumSquares += v * v;
        }
        const rms = Math.sqrt(sumSquares / data.length);

        // Boost and clamp — raw RMS from mic rarely exceeds 0.3
        const normalized = Math.min(1, rms * 4);

        const container = this.elements.waveformLive[0];
        if (!container) return;

        const BAR_MIN = 4;
        const BAR_MAX = 28;
        const height = Math.max(BAR_MIN, Math.round(normalized * BAR_MAX));

        const bar = document.createElement('div');
        bar.className = 'flex-shrink-0 w-1.5 rounded-sm bg-red-400';
        bar.style.height = height + 'px';
        container.appendChild(bar);

        // Auto-scroll: keep the latest bars visible by scrolling to the end
        container.scrollLeft = container.scrollWidth;
    }

    clearLiveWaveform() {
        const container = this.elements.waveformLive[0];
        if (container) container.innerHTML = '';
    }

    // --- Timer ---

    startTimer() {
        this.elapsedTime = 0;
        this.timerTick = 0;
        this.elements.timer.text(formatMinutesSeconds(0));

        this.timerInterval = setInterval(() => {
            this.timerTick++;
            this.elapsedTime += 0.1;
            this.elements.timer.text(formatMinutesSeconds(this.elapsedTime));

            // Append a waveform bar on every other tick (~5 bars/sec)
            if (this.isRecording && this.timerTick % 2 === 0) {
                this.sampleWaveform();
            }

            // Enable send button after MIN_DURATION
            if (this.isRecording && this.elapsedTime >= AudioUploadConfig.MIN_DURATION) {
                this.elements.sendBtn.prop('disabled', false);
                this.elements.sendBtn.removeClass('disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed disabled:hover:scale-100');
            }

            // Auto-stop at MAX_DURATION
            if (this.isRecording && this.elapsedTime >= AudioUploadConfig.MAX_DURATION + 0.5) {
                this.stopRecording();
            }
        }, 100);
    }

    stopTimer() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
    }

    // --- UI state management ---

    showRecordingUI() {
        this.elements.composeForm.addClass('hidden');
        this.elements.recordContainer.removeClass('hidden');

        // Ensure controls are visible (not sending state)
        this.elements.controls.removeClass('hidden');
        this.elements.sendingState.addClass('hidden');

        // Show stop button, hide play button
        this.elements.stopBtn.removeClass('hidden');
        this.elements.playBtn.addClass('hidden');

        // Disable send button until MIN_DURATION
        this.elements.sendBtn.prop('disabled', true);

        // Show live waveform + recording indicator
        this.clearLiveWaveform();
        this.elements.waveformLive.removeClass('hidden');
        this.elements.recIndicator.removeClass('hidden');

        // Reset timer display
        this.elements.timer.text(formatMinutesSeconds(0));

        // Initialize Lucide icons in the recording container
        window.LucideIcons?.initialize(this.elements.recordContainer[0]);
    }

    showPreviewUI() {
        // Hide stop, show play
        this.elements.stopBtn.addClass('hidden');
        this.elements.playBtn.removeClass('hidden');

        // Enable send button
        this.elements.sendBtn.prop('disabled', false);

        // Hide recording indicator, keep waveform visible as a static preview
        this.elements.recIndicator.addClass('hidden');

        // Re-initialize Lucide icons for play button
        window.LucideIcons?.initialize(this.elements.recordContainer[0]);
    }

    /* lucide-icons: circle-play, circle-stop */
    showPlayButton() {
        this.elements.playBtn.removeClass('hidden');
        window.LucideIcons?.swap(this.elements.playBtn[0].querySelector('[data-lucide]'), 'circle-play');
        this.elements.playBtn.attr('title', t('legacy.pm.play_audio'));
        this.elements.playBtn.attr('aria-label', t('legacy.pm.play_audio'));
    }

    showStopPlaybackButton() {
        window.LucideIcons?.swap(this.elements.playBtn[0].querySelector('[data-lucide]'), 'circle-stop');
        this.elements.playBtn.attr('title', t('legacy.pm.stop_playback'));
        this.elements.playBtn.attr('aria-label', t('legacy.pm.stop_playback'));
    }

    showUploadingUI() {
        this.elements.controls.addClass('hidden');
        this.elements.sendingState.removeClass('hidden');
    }

    hideRecordingUI() {
        this.elements.recordContainer.addClass('hidden');
        this.elements.composeForm.removeClass('hidden');

        // Reset sending state for next use
        this.elements.sendingState.addClass('hidden');
        this.elements.controls.removeClass('hidden');

        // Focus message input on desktop
        if (window.matchMedia('(min-width: 640px)').matches) {
            setTimeout(() => $('#message').focus(), 200);
        }
    }

    // --- Cleanup ---

    cleanup() {
        this.stopTimer();
        this.stopStream();
        this.teardownAnalyser();
        this.clearLiveWaveform();

        // Revoke object URL if set
        const audioEl = this.elements.audioPreview[0];
        if (audioEl && audioEl.src && audioEl.src.startsWith('blob:')) {
            URL.revokeObjectURL(audioEl.src);
            audioEl.src = '';
        }

        this.audioChunks = [];
        this.audioBlob = null;
        this.audioId = null;
        this.uploadResult = null;
        this.isRecording = false;
        this.isPlaying = false;
        this.sendAfterStop = false;
        this.elapsedTime = 0;
        this.elements.audioIdInput.val('');
    }

    stopStream() {
        if (this.stream) {
            this.stream.getTracks().forEach(track => track.stop());
            this.stream = null;
        }
    }

    // --- Public API ---

    hasAudio() {
        return this.audioBlob !== null || this.audioId !== null;
    }

    getAudioId() {
        return this.audioId;
    }

    isRecordingActive() {
        return this.isRecording;
    }
}

// Global initialization
function initAudioRecord() {
    const audioRecorder = new AudioRecorder();

    window.audioRecord = {
        hasAudio: audioRecorder.hasAudio.bind(audioRecorder),
        getAudioId: audioRecorder.getAudioId.bind(audioRecorder),
        isRecordingActive: audioRecorder.isRecordingActive.bind(audioRecorder)
    };

    return audioRecorder;
}

// Auto-initialize when PM page is loaded
if (typeof $ !== 'undefined') {
    $(document).ready(() => {
        if ($('.privateMessages').length > 0) {
            initAudioRecord();
        }
    });
}

if (typeof module !== 'undefined' && module.exports) {
    module.exports = { AudioRecorder };
}
